import { computed, watch, ref, type Ref, shallowRef, onBeforeUnmount, onMounted, nextTick } from 'vue';
import { useRoute } from 'vue-router'
import type { ICustomModalState, IDefaultModalState, ExposeInterface } from './types';
import { useNuxtApp } from '#app';
import { Strings } from "@/locales/types/enums";
import { useFocus } from "@/composables/useFocus";
import { publisher } from "@ca.vmedia.tv/domains";
import { linkItems } from "@/components/Header/types/LinkItemsCollection";
import { ClientMessageType } from "@ca.vmedia.tv/swagger-api";
import { TimeUtils, Logger } from '@/utils';
import {
    CustomModal,
    LazyTooltipe,
    LazyParentControlModal,
    LazyProgramModal,
    LazyParentForgotModal,
    LazySeasonModal,
    LazyAlertModal,
    LazyClientMessageModal,
    LazyGlobalMaintenanceModal,
} from '#components';
import { useApplicationStore } from "@/stores/Application";
import { useAccountStore } from "@/stores/Account";
import { storeToRefs } from 'pinia';
import { useParentControlStore } from "@/stores/ParentControl";
import { usePlayerStore } from "@/stores/Player";
import { useDetailPageStore } from "@/stores/DetailPage";
import { useClientMessagesStore } from "@/stores/ClientMessages";
import { ClientMessageModalActions } from "@/components/ClientMessageModal/types";
import type { AxiosError } from 'axios';


export const useGlobalModals = (): ExposeInterface => {
    const route = useRoute();
    const ApplicationStore = useApplicationStore();
    const {
        getIsModalReloadAppShow,
        getShowingTooltipeTime,
        getTooltipeShowing: tooltipeShowing,
        getIsInternetModalShow: isInternetModalShow,
        getIsOktaServerErrorModal,
        getIsOktaTokenErrorModal,
        getIsPreviewSingInModal,
        getCurrentSelectedIdProgram: selectedIdProgram,
        getIsModalSignInUnavailableShown,
        getIsModalExpiredCodeShown,
        getIsModalSignOutShown,
        getIsModalExitAppShown,
        getIsGlobalMaintenanceModalShow,
    } = storeToRefs(ApplicationStore);
    const { 
        setIsHaveFocusedElement,
        closeModalExitApp,
        deleteSelectedIdProgram,
        showPreviewSignInModal,
        showOktaTokenErrorModal,
        showServerErrorModal,
        setTooltipeShowing,
        closeModalSignOut,
        closeExpiredCode,
        closeSignInUnavailable,
        closeModalReloadApp,
    } = ApplicationStore;

    const ClientMessagesStore = useClientMessagesStore();
    const {
        markRead,
        closeUnreadMessages,
        setIsShowModal,
    } = ClientMessagesStore;
    const {
        lastMessage: apiMessage,
        hasMessages: hasApiMessages
    } = storeToRefs(ClientMessagesStore);

    const PlayerStoreModule = usePlayerStore();
    const {
        playerAvailability,
        isAlertModalVisible
    } = storeToRefs(PlayerStoreModule);


    const DetailPage = useDetailPageStore();
    const {
        getSeasonModalVisible,
        getProgramModalVisible
    } = storeToRefs(DetailPage);

    const { focusToHeader } = useFocus();

    const AccountStoreModule = useAccountStore();
    const {
        SetStatus,
        FetchVerificationCode,
        Logout
    } = AccountStoreModule;
    const {
        getForgotModalVisible
    } = storeToRefs(AccountStoreModule);

    const ParentControlStoreModule = useParentControlStore();
    const {
        SetIncorrectAdultPin,
        HideParentModal
    } = ParentControlStoreModule;
    const {
        getIsModalParentVisible,
    } = storeToRefs(ParentControlStoreModule);

    const app = useNuxtApp();

    const documentActiveElement = ref<HTMLElement | null>(null);
    const debounce = ref<ReturnType<typeof setTimeout> | null>(null);
    const apiMessageModalLoading = ref<boolean>(false);
    const isApiMessageModalVisible = ref<boolean>(false);

    const focusId= computed((): HTMLElement | null => document.getElementById("header-menu") || document.getElementById('playBtn') || document.getElementById('addBtn'));

    const getLocalesString = (name: string): ComputedRef<string> => {
        return computed(() => app.$i18n.t(name).toString()) 
    };


    const onDialogModalClick = async (answer: string) => {
        if (answer === app.$i18n.t(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE).toString()) {
            window.close();
        } else {
            closeModalExitApp();
            focusToHeader();
        }
    }

    const onSignInUnavailableModalClick = async (answer: string) => {
        if (answer === app.$i18n.t(Strings.SETTINGS_ACCOUNT_MODAL_CLOSE).toString()) {
            closeSignInUnavailable();
        } else {
            closeSignInUnavailable();
            await FetchVerificationCode();
        }
    }
    const onExpiredCodeModalClick = async (answer: string) => {
        if (answer === app.$i18n.t(Strings.SETTINGS_ACCOUNT_MODAL_CLOSE).toString()) {
            closeExpiredCode();
        } else {
            closeExpiredCode();
            await FetchVerificationCode();
        }
    }
    const onNoInternetModalClick = async (answer: string) => {
        if (answer === app.$i18n.t(Strings.SETTINGS_ACCOUNT_MODAL_CLOSE).toString()) {
            window.close();
        }
    }
    const focusElementLine = () => {
        const guide = document.getElementsByClassName('program-guide');
        const pageContent = document.getElementById('page-content');

        if (guide.length > 0 && pageContent) {
            pageContent.focus(); 
        } else {
            document.getElementById(selectedIdProgram.value!)?.focus();
            deleteSelectedIdProgram();
            setIsHaveFocusedElement(false);
        }
    }
    const onPreviewSignIn = async (answer: string) => {
        if (answer === app.$i18n.t(Strings.LOGIN_SIGNIN).toString()) {
            app.$router.push(linkItems.login.routerPath);
            showPreviewSignInModal(false);
        } else {
            showPreviewSignInModal(false);
            focusElementLine();
        }
    }
    const onSignOutModalClick = async (answer: string) => {
        if (answer === app.$i18n.t(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE)) {
            app.$eventBus.emit("data-loading", true);
            await app.$router.push(linkItems.login.routerPath);
            await Logout();
            closeModalSignOut();
            app.$eventBus.emit("data-loading", false);
        } else {
            closeModalSignOut();
            document.getElementById("btnAccountPage")?.focus();
        }
    }
    const onOktaTokenErrorModalClick = async (answer: string) => {
        if (answer === app.$i18n.t(Strings.LOGIN_OKTA_TOKEN_ERROR_BUTTON).toString()) {
            app.$router.push(linkItems.login.routerPath);
            showOktaTokenErrorModal(false);
        }
    }
    const onOktaTokenServerModalClick = async (answer: string) => {
        if (answer === app.$i18n.t(Strings.LOGIN_OKTA_SERVER_ERROR_BUTTON).toString()) {
            app.$router.push(linkItems.login.routerPath);
            showServerErrorModal(false);
        }
    }
    const onReloadAppModalClick = (answer: string) => {
        if (answer === app.$i18n.t(Strings.RELOAD_MODAL_BTN_CANCEL).toString()) {
            closeModalReloadApp();
            focusToHeader();
        } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore:next-line
            window.location.reload(true);
        }
    }
    const onMessagesModalClick = async (answer: string): Promise<void> => {
        if (answer === ClientMessageModalActions.SkipMessage) {
            if (apiMessage.value?.type === ClientMessageType.BlockingMessage) {
                return;
            }
            closeUnreadMessages([apiMessage.value?.id ?? 0]);
            return;
        }
        if (apiMessage.value?.type === ClientMessageType.BlockingMessage && Date.parse(apiMessage.value.expires ?? "") > TimeUtils.dateNow()) {
            window.close();
        } else {
            try {
                apiMessageModalLoading.value = true;
                await markRead([apiMessage.value?.id ?? 0]);
            } catch (e) {
                Logger.captureError(e as AxiosError);
            } finally {
                apiMessageModalLoading.value = false;
            }
        }
    }
    const onChangeMessagesModalShown = (_flag: boolean) => {
        isApiMessageModalVisible.value = _flag;
        if (_flag) {
            documentActiveElement.value = document.activeElement as HTMLElement;
            app.$navigator.clearEvents();
            setIsShowModal(true);
            return;
        }
        if (documentActiveElement.value && documentActiveElement.value.tagName !== 'BODY') {
            documentActiveElement.value?.focus();
        } else {
            focusId.value?.focus();
        }
        setIsShowModal(false);
    };
    const closeAllModal = (): void => {
        closeModalExitApp();
        HideParentModal();
        SetIncorrectAdultPin();
    }

    const customModals: Ref<(ICustomModalState)[]> = ref([
        {
            component: shallowRef(CustomModal),
            isShown: getIsModalExitAppShown,
            title: getLocalesString(Strings.HOME_EXIT_APP),
            message: getLocalesString(Strings.HOME_CONFIRM_EXIT_APP),
            buttons: [
                getLocalesString(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE),
                getLocalesString(Strings.SETTINGS_ACCOUNT_MODAL_NEGATIVE),
            ],
            name: 'exit',
            clickHandler: onDialogModalClick
        },
        {
            component: shallowRef(CustomModal),
            isShown: getIsModalSignInUnavailableShown,
            title: getLocalesString(Strings.LOGIN_SIGN_IN_UNAVAILABLE),
            message: "",
            buttons: [
                getLocalesString(Strings.SETTINGS_ACCOUNT_MODAL_CLOSE),
                getLocalesString(Strings.SETTINGS_ACCOUNT_MODAL_RETRY),
            ],
            name: 'signInUnavailable',
            clickHandler: onSignInUnavailableModalClick
        },
        {
            component: shallowRef(CustomModal),
            isShown: getIsModalExpiredCodeShown,
            title: getLocalesString(Strings.LOGIN_SIGN_IN_CODE_EXPIRED),
            message: "",
            buttons: [
                getLocalesString(Strings.SETTINGS_ACCOUNT_MODAL_CLOSE),
                getLocalesString(Strings.LOGIN_SIGN_IN_GET_NEW_CODE),
            ],
            name: 'expiredCode',
            clickHandler: onExpiredCodeModalClick
        },
        {
            component: shallowRef(CustomModal),
            isShown: isInternetModalShow,
            title: getLocalesString(Strings.HOME_NO_INTERNET),
            message: getLocalesString(Strings.HOME_CHECK_CONNECTION),
            buttons: [
                getLocalesString(Strings.SETTINGS_ACCOUNT_MODAL_CLOSE),
            ],
            name: 'internet',
            clickHandler: onNoInternetModalClick
        },
        {
            component: shallowRef(CustomModal),
            isShown: getIsPreviewSingInModal,
            title: getLocalesString(Strings.PREVIEW_MODE),
            message: getLocalesString(Strings[`CONTENT_UNAVAILABLE_SIGN_IN_${publisher}` as keyof typeof Strings]),
            buttons: [
                getLocalesString(Strings.LOGIN_SIGNIN),
                getLocalesString(Strings.CANCEL_BUTTON),
            ],
            name: 'previewSignIn',
            clickHandler: onPreviewSignIn
        },
        {
            component: shallowRef(CustomModal),
            isShown: getIsModalSignOutShown,
            title: '',
            message: getLocalesString(Strings.SETTINGS_ACCOUNT_MODAL_TXT),
            buttons: [
                getLocalesString(Strings.SETTINGS_ACCOUNT_MODAL_NEGATIVE),
                getLocalesString(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE),
            ],
            name: 'settings',
            focusedInd: 1,
            clickHandler: onSignOutModalClick
        },
        {
            component: shallowRef(CustomModal),
            isShown: getIsOktaTokenErrorModal,
            title: getLocalesString(Strings.LOGIN_OKTA_TOKEN_ERROR_TITLE),
            message: getLocalesString(Strings.LOGIN_OKTA_TOKEN_ERROR_TEXT),
            buttons: [
                getLocalesString(Strings.LOGIN_OKTA_TOKEN_ERROR_BUTTON),
            ],
            name: 'oktaTokenError',
            clickHandler: onOktaTokenErrorModalClick
        },
        {
            component: shallowRef(CustomModal),
            isShown: getIsOktaServerErrorModal,
            title: getLocalesString(Strings.LOGIN_OKTA_SERVER_ERROR_TITLE),
            message: getLocalesString(Strings.LOGIN_OKTA_SERVER_ERROR_TEXT),
            buttons: [
                getLocalesString(Strings.LOGIN_OKTA_SERVER_ERROR_BUTTON),
            ],
            name: 'oktaServerError',
            clickHandler: onOktaTokenServerModalClick
        },
        {
            component: shallowRef(CustomModal),
            isShown: getIsModalReloadAppShow,
            title: getLocalesString(Strings.RELOAD_MODAL_TITLE),
            message: getLocalesString(Strings.RELOAD_MODAL_MESSAGE),
            buttons: [
                getLocalesString(Strings.RELOAD_MODAL_BTN_CONFIRM),
                getLocalesString(Strings.RELOAD_MODAL_BTN_CANCEL),
            ],
            name: 'modalReloadApp',
            clickHandler: onReloadAppModalClick
        },
        {
            component: shallowRef(LazyClientMessageModal),
            isShown: isApiMessageModalVisible,
            title: null,
            message: null,
            buttons: null,
            apiMessage: apiMessage,
            name: 'apiMessage',
            loading: apiMessageModalLoading,
            clickHandler: onMessagesModalClick
        },
    ]);
    const defaultModals: Ref<IDefaultModalState[]> = ref([
        {
            component: shallowRef(LazyGlobalMaintenanceModal),
            isShown: getIsGlobalMaintenanceModalShow,
            name: 'globalMaintenance',
        },
        {
            component: shallowRef(LazyAlertModal),
            isShown: isAlertModalVisible,
        },
        {
            component: shallowRef(LazyParentControlModal),
            isShown: getIsModalParentVisible,
            closeHandler: focusElementLine
        },
        {
            component: shallowRef(LazyParentForgotModal),
            isShown: getForgotModalVisible,
        },
        {
            component: shallowRef(LazyProgramModal),
            isShown: getProgramModalVisible,
        },
        {
            component: shallowRef(LazySeasonModal),
            isShown: getSeasonModalVisible,
        },
        {
            component: shallowRef(LazyTooltipe),
            isShown: tooltipeShowing,
        },
    ]);


    watch(isInternetModalShow, (value: boolean): void => {
        if (!value && route.name !== 'player' && !playerAvailability.value) {
            SetStatus();
        }
        closeAllModal();
        focusId.value?.focus();
    });
    watch(tooltipeShowing, (value: boolean): void => {
        if (value) {
            setTimeout(() => {
                setTooltipeShowing({visible: false, showingTime: 5000});
            }, getShowingTooltipeTime.value);
        }
    });
    watch(hasApiMessages, (value: boolean): void => {
        onChangeMessagesModalShown(value);
    });
    onMounted(() => {
        nextTick(() => {
            debounce.value = setTimeout(() => {
                if (hasApiMessages.value) {
                    onChangeMessagesModalShown(true);
                }
            }, 5000);
        });
    });
    onBeforeUnmount(() => {
        closeAllModal();
    });
    return {
        customModals,
        defaultModals,
    }
}